(() => {
  'use strict';
  const root = () => document.body.dataset.root || '/';
  const t = (key, fallback) => window.VenluI18n.t('r24.' + key, {}, fallback);
  const uncertain = () => t('s_f4267f9847de', 'The connection was interrupted. Check your cart before adding again.');
  const saved = () => t('s_873208b2c409', 'Your change is saved. This view could not refresh. Please open the cart page.');
  const unavailable = () => t('s_54e071ddebdc', 'This size or quantity is unavailable. Please adjust your selection.');
  // Identity survives discount-related line-key changes; properties and plans never disappear.
  const identity = item => JSON.stringify([Number(item.variant_id || item.id), Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)), Number(item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || 0)]);
  const quantities = cart => {
    if (!Array.isArray(cart?.items)) throw Error('Missing cart items');
    const totals = new Map();
    cart.items.forEach(item => totals.set(identity(item), (totals.get(identity(item)) || 0) + Number(item.quantity)));
    return totals;
  };
  const matches = (cart, expected) => {
    const actual = quantities(cart);
    return actual.size === expected.size && [...expected].every(([key, count]) => actual.get(key) === count);
  };
  class VenluCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true; this.dialog = this.querySelector('dialog'); this.busy = false; this.serial = 0;
      this.onOpen = event => {
        const link = event.target.closest('[data-cart-open],.cart-success');
        if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault(); this.open(link);
      };
      /* R47 (Z-Build 23.10.): Schublade öffnet nach dem Hinzufügen auch mobil (vorher nur Hinweis-Toast). */
      this.onAdded = event => { this.open(event.detail?.trigger || document.activeElement); };
      this.onClick = event => {
        if (event.target.closest('[data-cart-close]')) this.close();
        const button = event.target.closest('[data-cart-change]');
        if (button && !this.locked()) this.mutate('cart/change.js', { id: button.dataset.key, quantity: Number(button.dataset.cartChange) }, button);
        if (event.target === this.dialog) {
          const r = this.dialog.getBoundingClientRect();
          if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.close();
        }
      };
      this.onSubmit = event => {
        const form = event.target.closest('[data-cart-upsell]'); if (!form) return;
        event.preventDefault();
        if (!this.locked()) this.mutate('cart/add.js', { items: [{ id: Number(form.elements.id.value), quantity: 1 }] }, form.querySelector('[type=submit]'));
      };
      this.onLanguage = () => {
        this.invalidateRead();
        if (this.busy) { this.languagePending = true; return; }
        if (this.dialog.open && !this.pendingMutation) this.refresh().catch(() => this.readFailure());
      };
      this.onPageHide = () => {
        this.lifecycle = (this.lifecycle || 0) + 1;
        this.suspended = true; this.invalidateRead(); this.writeController?.abort();
        if (this.dialog.open) this.dialog.close();
      };
      this.onPageShow = () => { this.suspended = false; this.syncLocks(); };
      document.addEventListener('click', this.onOpen); document.addEventListener('venlu:cart-added', this.onAdded);
      document.addEventListener('venlu:languagechange', this.onLanguage);
      this.addEventListener('click', this.onClick); this.addEventListener('submit', this.onSubmit);
      this.onClose = () => this.afterClose(); this.dialog.addEventListener('close', this.onClose);
      addEventListener('pagehide', this.onPageHide); addEventListener('pageshow', this.onPageShow); this.syncLocks();
    }
    disconnectedCallback() {
      this.lifecycle = (this.lifecycle || 0) + 1;
      document.removeEventListener('click', this.onOpen); document.removeEventListener('venlu:cart-added', this.onAdded);
      document.removeEventListener('venlu:languagechange', this.onLanguage);
      this.removeEventListener('click', this.onClick); this.removeEventListener('submit', this.onSubmit);
      this.dialog.removeEventListener('close', this.onClose); removeEventListener('pagehide', this.onPageHide); removeEventListener('pageshow', this.onPageShow);
      this.invalidateRead(); this.writeController?.abort(); this.afterClose(); this.ready = false;
    }
    locked() { return this.busy || this.hasAttribute('data-cart-recovery-required'); }
    syncLocks() {
      this.querySelectorAll('[data-cart-change],[data-cart-edit],[data-cart-upsell] button,[data-cart-upsell] select,button[name="checkout"]').forEach(el => el.disabled = this.locked());
      document.querySelectorAll('#vn-cart-form [data-cart-edit],#vn-cart-form button[type="submit"]').forEach(el => el.disabled = this.locked());
      document.querySelectorAll('#vn-cart-form .r19-express').forEach(el => el.inert = this.locked());
    }
    setBusy(value) { this.busy = value; this.setAttribute('aria-busy', String(value)); this.syncLocks(); }
    setRecovery(value) { this.toggleAttribute('data-cart-recovery-required', value); this.syncLocks(); }
    open(trigger) {
      if (!this.dialog.open) {
        this.querySelector('.r35-cart-extras')?.removeAttribute('open');
        this.trigger = trigger; this.previousOverflow = document.body.style.overflow; document.body.style.overflow = 'hidden';
        this.dialog.showModal(); document.querySelectorAll('[data-cart-open]').forEach(el => el.setAttribute('aria-expanded', 'true'));
        this.querySelector('[data-cart-close]').focus({ preventScroll: true });
      }
      if (!this.busy && !this.pendingMutation) this.refresh().catch(() => this.readFailure());
    }
    close() { this.dialog.close(); }
    afterClose() {
      if (this.previousOverflow !== undefined) { document.body.style.overflow = this.previousOverflow; this.previousOverflow = undefined; }
      document.querySelectorAll('[data-cart-open]').forEach(el => el.setAttribute('aria-expanded', 'false'));
      if (this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
    }
    message(text, error = false) {
      const status = this.querySelector('[data-cart-status]'); status.textContent = text; status.dataset.error = String(error);
      this.querySelector('[data-cart-recovery]').hidden = !error;
      const pageStatus = document.querySelector('[data-cart-page-status]'); if (pageStatus) pageStatus.textContent = text;
      const pageRecovery = document.querySelector('[data-cart-page-recovery]'); if (pageRecovery) pageRecovery.hidden = !error;
    }
    readFailure() { this.message(t('s_bc1cd4ff8dfc', 'Could not refresh your cart. Please retry or open the full cart.'), true); }
    invalidateRead() { this.serial++; this.readController?.abort(); this.readRequest = null; }
    async request(url, options = {}, type = 'json', controller = new AbortController()) {
      if (this.suspended) throw Error('Cart page suspended');
      const lifecycle = this.lifecycle || 0;
      // Deadline includes body parsing, not just arrival of response headers.
      let timer;
      const deadline = new Promise((_, reject) => {
        timer = setTimeout(() => { controller.abort(); reject(Error('Cart request timed out')); }, options.method === 'POST' ? 20000 : 10000);
      });
      try {
        return await Promise.race([deadline, (async () => {
          const response = await fetch(url, { ...options, signal: controller.signal });
          const data = await response[type]();
          if (this.suspended || lifecycle !== (this.lifecycle || 0)) throw Error('Cart page suspended');
          return { ok: response.ok, status: response.status, data };
        })()]);
      } finally { clearTimeout(timer); }
    }
    async readCart() {
      const result = await this.request(root() + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!result.ok || !Array.isArray(result.data?.items)) throw Error('Cart unavailable');
      return result.data;
    }
    renderHTML(html) {
      if (typeof html !== 'string') throw Error('Section missing');
      const parsed = new DOMParser().parseFromString(html, 'text/html'), render = parsed.querySelector('[data-cart-render]');
      const count = Number(render?.dataset.cartItemCount);
      if (!render || !Number.isInteger(count) || count < 0) throw Error('Invalid cart section');
      const wasDone = this.querySelector('[data-r19-ship]')?.dataset.remaining === '0';
      const scroll = this.querySelector('.vn-cart-scroll')?.scrollTop || 0;
      this.querySelector('[data-cart-render]').replaceWith(render);
      const area = this.querySelector('.vn-cart-scroll'); if (area) area.scrollTop = scroll;
      document.querySelectorAll('[data-cart-count]').forEach(el => el.textContent = count);
      if (!wasDone && this.querySelector('[data-r19-ship]')?.dataset.remaining === '0') this.querySelector('[data-cart-shipping-goal]')?.classList.add('is-newly-done');
      this.syncLocks(); return { item_count: count };
    }
    refresh() {
      if (this.readRequest) return this.readRequest;
      const serial = ++this.serial, requestRoot = root(), controller = new AbortController(); this.readController = controller;
      const url = new URL(requestRoot, location.origin); url.searchParams.set('section_id', this.dataset.sectionId);
      const task = this.request(url, { cache: 'no-store' }, 'text', controller).then(result => {
        if (serial !== this.serial || requestRoot !== root() || controller.signal.aborted) return;
        if (!result.ok) throw Error('Section unavailable');
        const cart = this.renderHTML(result.data);
        if (!this.pendingMutation) {
          if (this.hasAttribute('data-cart-recovery-required')) this.message('');
          this.setRecovery(false);
        }
        return cart;
      }).catch(error => { if (serial !== this.serial || controller.signal.aborted) return; throw error; }).finally(() => { if (this.readRequest === task) this.readRequest = null; });
      this.readRequest = task; return task;
    }
    async post(route, body) {
      const controller = new AbortController(); this.writeController = controller;
      try {
        return await this.request(root() + route, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ ...body, sections: [this.dataset.sectionId], sections_url: root() }) }, 'json', controller);
      } finally { if (this.writeController === controller) this.writeController = null; }
    }
    async showResult(result, requestRoot) {
      if (!this.languagePending && requestRoot === root()) {
        try { this.renderHTML(result?.sections?.[this.dataset.sectionId]); return; } catch { /* Null HTML does not mean the POST failed. */ }
      }
      // One bounded read, never a second mutation or a retry loop.
      this.languagePending = false;
      if (!await this.refresh()) throw Error('Cart refresh superseded');
    }
    focusLine(key) {
      if (!this.dialog.open) return;
      const line = [...this.querySelectorAll('[data-cart-key]')].find(el => el.dataset.cartKey === key);
      (line?.querySelector('[data-cart-edit],[data-cart-change]') || this.querySelector('[data-cart-close]'))?.focus({ preventScroll: true });
    }
    async mutate(route, body, trigger) {
      if (this.locked()) return;
      this.invalidateRead(); this.setBusy(true); this.pendingMutation = { route, body };
      this.message(t('s_ea4e7e65e867', 'Updating …'));
      let committed = false;
      try {
        const requestRoot = root(), response = await this.post(route, body);
        if (!response.ok) throw Error(typeof response.data?.description === 'string' ? response.data.description : unavailable());
        committed = true; this.pendingMutation = null;
        await this.showResult(response.data, requestRoot);
        this.message('');
        document.dispatchEvent(new CustomEvent('venlu:cart-updated'));
      } catch (error) {
        // An unconfirmed POST gets one read-only recovery attempt, never another POST.
        if (!committed && !this.suspended) { try { await this.refresh(); } catch { /* Full-cart recovery remains available. */ } }
        this.setRecovery(true);
        this.message(committed ? saved() : (error.message && !/timed out|fetch|JSON/i.test(error.message) ? error.message + ' ' : '') + uncertain(), true);
      } finally { this.setBusy(false); this.focusLine(trigger?.dataset.key); }
    }
    async replaceLine(line, chosen, productURL) {
      if (this.locked()) return { ok: false, recovery: true, error: uncertain() };
      if (Number(line.variantId) === Number(chosen.id)) return { ok: true };
      this.invalidateRead(); this.setBusy(true);
      const requestRoot = root(); let wrote = false, committed = false;
      try {
        const before = await this.readCart(), old = before.items.find(item => item.key === line.key);
        if (!old || Number(old.quantity) !== Number(line.quantity) || Number(old.product_id) !== Number(line.productId) || Number(old.variant_id) !== Number(line.variantId) || identity(old) !== identity({ id: line.variantId, properties: line.properties, selling_plan: line.sellingPlan })) throw Error(t('s_cfeff6b1f673', 'Check the pending cart transfer first.'));
        if (old.item_components?.length || old.parent_relationship) throw Error(unavailable());
        const url = new URL(productURL, location.origin);
        if (url.origin !== location.origin || !/\/products\/[^/]+$/.test(url.pathname)) throw Error('Invalid product');
        url.search = ''; url.pathname += '.js';
        const productResult = await this.request(url, { cache: 'no-store', headers: { Accept: 'application/json' } });
        const current = productResult.data?.variants?.find(item => Number(item.id) === Number(chosen.id));
        const plan = Number(old.selling_plan_allocation?.selling_plan?.id || 0);
        if (!productResult.ok || Number(productResult.data.id) !== Number(old.product_id) || !current?.available || (plan && !current.selling_plan_allocations?.some(item => Number(item.selling_plan_id || item.selling_plan?.id) === plan))) throw Error(unavailable());
        const oldIdentity = identity(old);
        if (before.items.filter(item => identity(item) === oldIdentity).length !== 1) throw Error(t('s_69179f127478', 'Check cart first'));
        const item = { id: Number(chosen.id), quantity: old.quantity, properties: old.properties || {} };
        if (plan) item.selling_plan = plan;
        const nextIdentity = identity(item), expectedAdded = quantities(before);
        expectedAdded.set(nextIdentity, (expectedAdded.get(nextIdentity) || 0) + old.quantity);
        this.pendingMutation = { phase: 'add-replacement', old: { ...old }, replacement: item };
        wrote = true;
        let addResponse;
        try { addResponse = await this.post('cart/add.js', { items: [item] }); } catch { /* One read reconciles, never replay the POST. */ }
        // add.js does not return the complete cart. An error can still have added available stock.
        const added = await this.readCart();
        if (!matches(added, expectedAdded)) {
          if (addResponse && [404, 422].includes(addResponse.status) && matches(added, quantities(before))) { this.pendingMutation = null; wrote = false; throw Error(typeof addResponse.data?.description === 'string' ? addResponse.data.description : unavailable()); }
          throw Error(uncertain());
        }
        const retained = added.items.filter(entry => identity(entry) === oldIdentity);
        if (retained.length !== 1 || retained[0].quantity !== old.quantity) throw Error(uncertain());
        const expectedFinal = new Map(expectedAdded); expectedFinal.delete(oldIdentity);
        this.pendingMutation.phase = 'remove-original'; this.pendingMutation.oldKey = retained[0].key;
        let changed;
        try {
          const result = await this.post('cart/change.js', { id: retained[0].key, quantity: 0 });
          if (result.ok && Array.isArray(result.data?.items) && matches(result.data, expectedFinal)) changed = result.data;
        } catch { /* The server may already have removed the old line. */ }
        if (!changed) {
          const reconciled = await this.readCart();
          if (!matches(reconciled, expectedFinal)) throw Error(uncertain());
          changed = reconciled;
        }
        committed = true; this.pendingMutation = null;
        await this.showResult(changed, requestRoot);
        this.setRecovery(false); this.message('');
        document.dispatchEvent(new CustomEvent('venlu:cart-updated'));
        return { ok: true, variantId: chosen.id, key: changed.items.find(entry => identity(entry) === nextIdentity)?.key };
      } catch (error) {
        if (wrote) this.setRecovery(true);
        const message = committed ? saved() : wrote ? uncertain() : error.message || unavailable();
        this.message(message, wrote); return { ok: false, recovery: wrote, error: message };
      } finally { this.setBusy(false); }
    }
  }
  if (!customElements.get('vn-cart-drawer')) customElements.define('vn-cart-drawer', VenluCartDrawer);
})();
